<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { FileText, Pencil, RotateCw, Send, ShieldCheck, ThumbsUp, User, Building2, Wallet, Trash2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { API, HOST } from '@/utils/http'
import type { Invoice } from '@/types/bridge'
import InvoiceCreateDialog from './InvoiceCreateDialog.vue'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

const currentEmail = computed<string>(() => userStore.member?.email?.toLowerCase() ?? '')

const invoices = ref<Invoice[]>([])
const userVerified = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)
const createDialog = ref<InstanceType<typeof InvoiceCreateDialog> | null>(null)
const confirmSendDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingSend = ref<Invoice | null>(null)
const confirmResendDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingResend = ref<Invoice | null>(null)
const confirmPayDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingPay = ref<Invoice | null>(null)
const confirmDeleteDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingDelete = ref<Invoice | null>(null)

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  approved: 'Approved',
  paid: 'Paid',
  cancelled: 'Cancelled',
}

const STATUS_CLASSES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
}

interface InvoiceSummaryStat {
  label: string
  count: number
  amountLabel: string
}

function summarizeInvoices(list: Invoice[]): { count: number; amountLabel: string } {
  const byCurrency = new Map<string, number>()
  for (const inv of list) {
    const amt = parseFloat(inv.amount) || 0
    byCurrency.set(inv.currency, (byCurrency.get(inv.currency) ?? 0) + amt)
  }
  const amountLabel = Array.from(byCurrency.entries())
    .map(([currency, sum]) => `${sum.toFixed(2)} ${currency}`)
    .join(', ') || '0.00'
  return { count: list.length, amountLabel }
}

const summaryStats = computed<InvoiceSummaryStat[]>(() => {
  const invoiced = invoices.value.filter(i => i.status === 'sent' || i.status === 'approved' || i.status === 'paid')
  const collected = invoices.value.filter(i => i.status === 'paid')
  const outstanding = invoices.value.filter(i => i.status === 'sent' || i.status === 'approved')
  return [
    { label: 'Invoiced', ...summarizeInvoices(invoiced) },
    { label: 'Collected', ...summarizeInvoices(collected) },
    { label: 'Outstanding', ...summarizeInvoices(outstanding) },
  ]
})

async function fetchInvoices() {
  loading.value = true
  error.value = null
  try {
    const res = await API.get(`${HOST}/payments/invoices`)
    userVerified.value = res.data.user_verified
    invoices.value = Array.isArray(res.data.invoices) ? res.data.invoices : []
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to load invoices.'
  } finally {
    loading.value = false
  }
}

function goToKyc() {
  router.push({ ...route, query: { ...route.query, mode: 'kyc' } })
}

function addInvoice(invoice: Invoice) {
  invoices.value.unshift(invoice)
}

function replaceInvoice(invoice: Invoice) {
  const idx = invoices.value.findIndex(i => i.id === invoice.id)
  if (idx !== -1) invoices.value[idx] = invoice
}

function removeInvoice(id: number) {
  invoices.value = invoices.value.filter(i => i.id !== id)
}

const sending = ref<number | null>(null)

const approving = ref<number | null>(null)

async function approveInvoice(inv: Invoice) {
  approving.value = inv.id
  try {
    const res = await API.post(`${HOST}/payments/invoices/${inv.id}/approve`)
    replaceInvoice(res.data)
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to approve invoice.'
  } finally {
    approving.value = null
  }
}

function promptSend(inv: Invoice) {
  pendingSend.value = inv
  confirmSendDialog.value?.open()
}

async function sendInvoice() {
  const inv = pendingSend.value
  if (!inv) return
  sending.value = inv.id
  try {
    const res = await API.post(`${HOST}/payments/invoices/${inv.id}/send`)
    replaceInvoice(res.data)
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to send invoice.'
  } finally {
    sending.value = null
    pendingSend.value = null
  }
}

function promptResend(inv: Invoice) {
  pendingResend.value = inv
  confirmResendDialog.value?.open()
}

async function resendInvoice() {
  const inv = pendingResend.value
  if (!inv) return
  sending.value = inv.id
  try {
    const res = await API.post(`${HOST}/payments/invoices/${inv.id}/resend`)
    replaceInvoice(res.data)
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to resend invoice.'
  } finally {
    sending.value = null
    pendingResend.value = null
  }
}

const paying = ref<number | null>(null)

function promptPay(inv: Invoice) {
  pendingPay.value = inv
  confirmPayDialog.value?.open()
}

async function payInvoice() {
  const inv = pendingPay.value
  if (!inv) return
  paying.value = inv.id
  try {
    const res = await API.post(`${HOST}/payments/invoices/${inv.id}/pay`)
    replaceInvoice(res.data)
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to pay invoice.'
  } finally {
    paying.value = null
    pendingPay.value = null
  }
}

const deleting = ref<number | null>(null)

function promptDelete(inv: Invoice) {
  pendingDelete.value = inv
  confirmDeleteDialog.value?.open()
}

async function deleteInvoice() {
  const inv = pendingDelete.value
  if (!inv) return
  deleting.value = inv.id
  try {
    await API.delete(`${HOST}/payments/invoices/${inv.id}`)
    removeInvoice(inv.id)
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to delete invoice.'
  } finally {
    deleting.value = null
    pendingDelete.value = null
  }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

onMounted(fetchInvoices)
defineExpose({ addInvoice })
</script>

<template>
  <div class="p-4 space-y-2">
    <div v-if="loading" class="space-y-2">
      <div v-for="i in 3" :key="i" class="h-14 bg-muted rounded animate-pulse" />
    </div>

    <div v-else-if="error" class="text-xs text-red-600">{{ error }}</div>

    <div v-else-if="!userVerified" class="flex flex-col items-center justify-center py-16 text-center gap-4">
      <ShieldCheck class="h-10 w-10 text-muted-foreground" />
      <p class="text-sm text-muted-foreground max-w-xs">Verify your identity to send and receive invoices.</p>
      <Button class="bg-blue-600 hover:bg-blue-700 text-white" @click="goToKyc">Complete Verification</Button>
    </div>

    <template v-else>
      <div v-if="invoices.length > 0" class="flex flex-wrap gap-6 rounded-lg border px-4 py-3 text-sm">
        <div v-for="stat in summaryStats" :key="stat.label">
          <div class="text-xs font-medium text-muted-foreground uppercase tracking-wide">{{ stat.label }}</div>
          <div class="font-semibold tabular-nums">
            {{ stat.amountLabel }}
            <span class="text-muted-foreground font-normal">({{ stat.count }})</span>
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <Button @click="createDialog?.show()">+ Invoice</Button>
      </div>

      <div v-if="invoices.length === 0" class="flex flex-col items-center justify-center py-16 text-center gap-4">
        <FileText class="h-10 w-10 text-muted-foreground" />
        <p class="text-sm text-muted-foreground">No invoices yet. Create one to request payment.</p>
      </div>

      <div
        v-for="inv in invoices"
        :key="inv.id"
        class="flex flex-wrap items-start justify-between gap-x-4 gap-y-1.5 rounded-lg border px-4 py-2 text-sm hover:bg-muted/30 transition-colors"
      >
        <div class="min-w-0 space-y-0.5">
          <div class="font-medium truncate">{{ inv.description || 'Invoice' }}</div>
          <div class="flex items-center gap-1 text-xs text-muted-foreground truncate">
            <span class="truncate">From: {{ inv.from_name ? `${inv.from_name} <${inv.from_email}>` : inv.from_email }}</span>
            <User v-if="inv.sender_type === 'individual'" class="h-3 w-3 shrink-0 text-blue-500" />
            <Building2 v-else-if="inv.sender_type === 'business'" class="h-3 w-3 shrink-0 text-violet-500" />
          </div>
          <div class="flex items-center gap-1 text-xs text-muted-foreground truncate">
            <span class="truncate">To: {{ inv.to_name ? `${inv.to_name} <${inv.to_email}>` : inv.to_email }}</span>
            <User v-if="inv.recipient_type === 'individual'" class="h-3 w-3 shrink-0 text-blue-500" />
            <Building2 v-else-if="inv.recipient_type === 'business'" class="h-3 w-3 shrink-0 text-violet-500" />
          </div>
        </div>
        <div class="flex items-start gap-3 shrink-0 ml-auto">
          <Button
            v-if="inv.status === 'approved' && !inv.ready_at && inv.to_email.toLowerCase() === currentEmail"
            size="sm"
            class="bg-green-600 hover:bg-green-700 text-white"
            :disabled="paying === inv.id"
            @click="promptPay(inv)"
          >
            <Wallet class="h-3.5 w-3.5 mr-1" :class="{ 'animate-pulse': paying === inv.id }" />
            Pay
          </Button>
          <div class="flex flex-col items-end gap-1.5 shrink-0">
            <span class="font-medium tabular-nums">
              {{ parseFloat(inv.amount).toFixed(2) }} {{ inv.currency }}
            </span>
            <!-- Draft: badge + date + edit/send actions -->
            <div v-if="inv.status === 'draft'" class="flex flex-wrap justify-end items-center gap-1.5 text-xs">
              <span class="px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-600">Draft</span>
              <span class="text-muted-foreground">{{ formatDate(inv.created_at) }}</span>
              <span v-if="inv.due_date" class="text-muted-foreground">· due {{ inv.due_date }}</span>
              <button
                class="text-muted-foreground hover:text-foreground transition-colors"
                title="Edit invoice"
                @click="createDialog?.show(inv)"
              >
                <Pencil class="h-3.5 w-3.5" />
              </button>
              <button
                class="text-muted-foreground hover:text-blue-600 transition-colors"
                title="Send invoice"
                :disabled="sending === inv.id"
                @click="promptSend(inv)"
              >
                <Send class="h-3.5 w-3.5" :class="{ 'animate-pulse': sending === inv.id }" />
              </button>
              <button
                class="text-muted-foreground hover:text-red-600 transition-colors"
                title="Delete invoice"
                :disabled="deleting === inv.id"
                @click="promptDelete(inv)"
              >
                <Trash2 class="h-3.5 w-3.5" :class="{ 'animate-pulse': deleting === inv.id }" />
              </button>
            </div>

            <!-- Sent+: always show Sent + date, then Approved below if applicable -->
            <template v-else>
              <div class="flex flex-wrap justify-end items-center gap-1.5 text-xs">
                <span class="px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700">Sent</span>
                <span class="text-muted-foreground">{{ formatDate(inv.created_at) }}</span>
                <span v-if="inv.due_date" class="text-muted-foreground">· due {{ inv.due_date }}</span>
                <button
                  v-if="(inv.status === 'sent' || inv.status === 'approved') && inv.from_email.toLowerCase() === currentEmail"
                  class="text-muted-foreground hover:text-blue-600 transition-colors"
                  title="Resend invoice"
                  :disabled="sending === inv.id"
                  @click="promptResend(inv)"
                >
                  <RotateCw class="h-3.5 w-3.5" :class="{ 'animate-pulse': sending === inv.id }" />
                </button>
              </div>
              <div v-if="inv.status === 'approved' && inv.ready_at" class="flex items-center gap-1.5 text-xs">
                <span class="px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700">
                  Paying · settles {{ formatDate(inv.ready_at) }}
                </span>
              </div>
              <div v-else-if="inv.status === 'approved'" class="flex items-center gap-1.5 text-xs">
                <span class="px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700">Approved</span>
              </div>
              <div v-else-if="inv.status === 'paid'" class="flex items-center gap-1.5 text-xs">
                <span class="px-1.5 py-0.5 rounded font-medium bg-green-100 text-green-700">Paid</span>
              </div>
              <div v-else-if="inv.status === 'sent' && inv.to_email.toLowerCase() === currentEmail" class="flex items-center gap-1.5 text-xs">
                <button
                  class="flex items-center gap-1 text-muted-foreground hover:text-amber-600 transition-colors"
                  title="Approve invoice"
                  :disabled="approving === inv.id"
                  @click="approveInvoice(inv)"
                >
                  <ThumbsUp class="h-3.5 w-3.5" :class="{ 'animate-pulse': approving === inv.id }" />
                  <span>Approve</span>
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>

    <InvoiceCreateDialog ref="createDialog" @created="addInvoice" @updated="replaceInvoice" />
    <DialogConfirm
      ref="confirmSendDialog"
      title="Send invoice?"
      :description="pendingSend ? `Send this invoice to ${pendingSend.to_email}? They'll be able to see it immediately.` : ''"
      confirmText="Send"
      @confirm="sendInvoice"
      @cancel="pendingSend = null"
    />
    <DialogConfirm
      ref="confirmResendDialog"
      title="Resend invoice?"
      :description="pendingResend ? `Resend this invoice to ${pendingResend.to_email}?` : ''"
      confirmText="Resend"
      @confirm="resendInvoice"
      @cancel="pendingResend = null"
    />
    <DialogConfirm
      ref="confirmPayDialog"
      title="Pay invoice?"
      :description="pendingPay ? `Pay ${parseFloat(pendingPay.amount).toFixed(2)} ${pendingPay.currency} to ${pendingPay.from_name || pendingPay.from_email}? Funds settle automatically after a short holding period — this can't be cancelled once started.` : ''"
      confirmText="Pay"
      @confirm="payInvoice"
      @cancel="pendingPay = null"
    />
    <DialogConfirm
      ref="confirmDeleteDialog"
      title="Delete invoice?"
      :description="pendingDelete ? `Delete this draft invoice to ${pendingDelete.to_email}? This can't be undone.` : ''"
      confirmText="Delete"
      variant="error"
      @confirm="deleteInvoice"
      @cancel="pendingDelete = null"
    />
  </div>
</template>
